// Usage watch (throwaway: backend/src/modules/usage/README.md). Reports each page the dashboard shows and how long
// it was in front of the operator. Draws nothing. Hidden tabs don't count: the clock stops while the tab is hidden.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const BASE = import.meta.env.VITE_API_URL ?? '/api';
const MIN_MS = 1500;

function send(path: string, ms: number) {
  if (path.startsWith('/usage') || ms < MIN_MS) return;
  try {
    void fetch(`${BASE}/usage/view`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: path.slice(0, 500), title: document.title.slice(0, 200), ms: Math.round(ms) }),
    }).catch(() => {});
  } catch {
    // Never in the operator's way.
  }
}

export default function UsageTracker() {
  const location = useLocation();
  const current = useRef<{ path: string; since: number | null; ms: number } | null>(null);

  useEffect(() => {
    const path = location.pathname + location.search;
    const prev = current.current;
    if (prev && prev.path !== path) {
      send(prev.path, prev.ms + (prev.since ? Date.now() - prev.since : 0));
    }
    if (!prev || prev.path !== path) current.current = { path, since: document.hidden ? null : Date.now(), ms: 0 };
  }, [location.pathname, location.search]);

  useEffect(() => {
    const pause = () => {
      const c = current.current;
      if (!c || c.since === null) return;
      c.ms += Date.now() - c.since;
      c.since = null;
      // Report now: the tab may never come back.
      send(c.path, c.ms);
      c.ms = 0;
    };
    const resume = () => {
      const c = current.current;
      if (c && c.since === null) c.since = Date.now();
    };
    const onVisibility = () => (document.hidden ? pause() : resume());
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', pause);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', pause);
    };
  }, []);

  return null;
}
